import type { EntityType } from '@/types/api';

/** <select> value standing in for rows whose parent reference is null/empty. */
export const UNASSIGNED = '__unassigned__';

export interface ParentFilterConfig {
  /** Row key holding the parent id. */
  idKey: string;
  /** Row key holding the parent name denormalized onto the row. */
  nameKey: string;
  /** Label for the "no filter" option. */
  allLabel: string;
  /** Accessible name for the <select>, which has no visible label. */
  ariaLabel: string;
}

/**
 * The entity tabs that get a parent-scope <select> in the toolbar.
 *
 * Both ids are read straight off the row rather than matched against the
 * sibling entity tab, because the tabs live in different id spaces:
 * synced_menu_groups.category_id is a POS category id (it matches
 * synced_menu_categories.pos_category_id, NOT its id), while a managed menu
 * item's groupId is a managed_menu_groups PK. Every row already carries the
 * parent's name alongside its id, so deriving options from the rows is
 * self-consistent in either space and needs no mapping layer.
 */
export const PARENT_FILTERS: Partial<Record<EntityType, ParentFilterConfig>> = {
  menuGroups: {
    idKey: 'categoryId',
    nameKey: 'categoryName',
    allLabel: 'All categories',
    ariaLabel: 'Filter menu groups by category',
  },
  menuItems: {
    idKey: 'groupId',
    nameKey: 'groupName',
    allLabel: 'All groups',
    ariaLabel: 'Filter menu items by menu group',
  },
};

export interface ParentOption {
  value: string;
  label: string;
  count: number;
}

/** Normalize a row's parent id into the string used as an <option> value. */
export function parentKeyOf(row: Record<string, unknown>, idKey: string): string {
  const raw = row[idKey];
  if (raw === null || raw === undefined || raw === '') return UNASSIGNED;
  return String(raw);
}

/**
 * The distinct parents present in `rows`, sorted by label with a row count
 * each. `UNASSIGNED` always sorts last so it never displaces a real parent.
 */
export function deriveParentOptions(
  rows: Record<string, unknown>[],
  config: ParentFilterConfig
): ParentOption[] {
  const byValue = new Map<string, ParentOption>();

  for (const row of rows) {
    const value = parentKeyOf(row, config.idKey);
    const existing = byValue.get(value);
    if (existing) {
      existing.count += 1;
      continue;
    }
    const name = String(row[config.nameKey] ?? '').trim();
    byValue.set(value, {
      value,
      // Fall back to the raw id so a row missing its denormalized name is
      // still selectable rather than showing a blank option.
      label: value === UNASSIGNED ? 'Unassigned' : name || `#${value}`,
      count: 1,
    });
  }

  return [...byValue.values()].sort((a, b) => {
    if (a.value === UNASSIGNED) return 1;
    if (b.value === UNASSIGNED) return -1;
    return a.label.localeCompare(b.label);
  });
}

/** Rows under `selected`; every row when `selected` is empty or unconfigured. */
export function applyParentFilter(
  rows: Record<string, unknown>[],
  config: ParentFilterConfig | undefined,
  selected: string
): Record<string, unknown>[] {
  if (!config || !selected) return rows;
  return rows.filter((row) => parentKeyOf(row, config.idKey) === selected);
}
